import { getLength } from 'ol/sphere';
import { Circle, LineString } from 'ol/geom';

/**
 * Calculates the radius of an OpenLayers circle in kilometers, accounting for projection distortion
 * @param circle - OpenLayers Circle geometry
 * @param displayProjection - The display projection (e.g., 'EPSG:3857')
 * @param dataProjection - The data projection (e.g., 'EPSG:4326')
 * @returns radius in kilometers, rounded to the nearest integer
 */
export const getCircleTrueRadius = (
  circle: Circle,
  displayProjection: string,
  dataProjection: string
): number => {
  // Get circle center and a point on the circle edge
  const center = circle.getCenter();
  const radius = circle.getRadius();
  const edgePoint = [center[0] + radius, center[1]];

  // Create a line from center to edge
  const line = new LineString([center, edgePoint]);

  // Transform to data projection for accurate distance calculation
  const transformedLine = line
    .clone()
    .transform(displayProjection, dataProjection);

  // Calculate length in meters
  const lengthInMeters = getLength(transformedLine, {
    projection: dataProjection,
    radius: 6378137, // Earth's radius in meters
  });

  // Convert to kilometers
  return Math.round(lengthInMeters);
};

/**
 * Converts a desired true radius in kilometers to the larger radius needed
 * in OpenLayers projection to account for projection distortion
 * @param trueRadiusMeters - The actual desired radius in meters
 * @param centerLatitude - The latitude of the circle's center (in degrees)
 * @param displayProjection - The display projection (e.g., 'EPSG:3857')
 * @param dataProjection - The data projection (e.g., 'EPSG:4326')
 * @returns The radius to use in OpenLayers to achieve the desired true radius
 */
export const getTrueToProjectedRadius = (
  trueRadiusMeters: number,
  centerLatitude: number,
  displayProjection: string,
  dataProjection: string
): number => {
  // Create a reference point at the given latitude
  const centerY =
    Math.log(Math.tan(Math.PI / 4 + (centerLatitude * Math.PI) / 180 / 2)) *
    6378137;
  const center: [number, number] = [0, centerY];

  const edgePoint = [center[0] + trueRadiusMeters, center[1]];
  const line = new LineString([center, edgePoint]);

  // Transform to get true length
  const transformedLine = line
    .clone()
    .transform(displayProjection, dataProjection);
  const measuredLength = getLength(transformedLine, {
    projection: dataProjection,
    radius: 6378137,
  });

  // Calculate scaling factor
  const scaleFactor = trueRadiusMeters / measuredLength;

  // Return adjusted radius
  return trueRadiusMeters * scaleFactor;
};
